import Tag from './Tag'
import ID from './ID'
import PlanDocument from './PlanDocument'
import isValidDate from '@/utils/isValidDate'
import { parseAllDayDate } from '@/utils/parseAllDayDate'
import PlanningItem, { InitiativeSummaryData } from './InterfacePlanningItem'
import Channel from './Channel'

export default class InitiativeCustomization implements PlanningItem {
  public editing = false
  public opened = false
  public newStartDate: Date
  public newEndDate: Date
  public newFile: any
  public newFileName: string
  public newSummary: string
  public initiativeSummary: InitiativeSummaryData | null = null

  constructor(
    public id: ID = new ID(),
    public creatorId: ID = new ID(),
    public initiativeId: ID = new ID(),
    public initiativeTypeId: ID = new ID(),
    public planId: ID = new ID(),
    public startDate: Date = new Date(Date.now()), // Overridable
    public endDate: Date = new Date(Date.now()), // Overridable
    public isAllDay: boolean = false, // Overridable
    public tagIds: ID[] = [],  // Add but not override
    public documentIds: ID[] = [], // Add but not override

    public summary: string = '', // Add but not override
    public additionalSummaryUrl: string = '', // Add but not override

    public tags: Tag[] = [], // Populated on front-end
    public documents: PlanDocument[] = [], // Populated on front-end
    public title: string = '', // Populated on front-end
    public initiativeTypeName: string = '', // Populated on front-end
    public abbreviatedPlanName: string = '', // Populated on front-end
    public fullPlanName: string = '', // Populated on front-end
    public leadInitiativeSummary: string = '', // Populated on front-end
    public leadInitiativeSummaryContentUrl: string | null = null, // Populated on front-end
  ) {
    this.newStartDate = startDate
    this.newEndDate = endDate
    this.newFile = null
    this.newFileName = ''
    this.newSummary = summary
  }

  public get key() {
    return 'initiative-customization-' + this.id.intID.toString()
  }

  public get channelName(): string {
    return Channel.CHANNEL_NAME_INITIATIVES
  }

  public get isLead(): boolean {
    return false
  }

  public get isNested(): boolean {
    return true
  }

  public get isInitiativeCustomization(): boolean {
    return true
  }

  public static fromResponseObject(obj): InitiativeCustomization {
    if (!obj) return new InitiativeCustomization()

    let startDateString = obj.startDate
    if (obj.startDate && obj.startDate.date) {
      startDateString = obj.startDate.date
    }

    let endDateString = obj.endDate
    if (obj.endDate && obj.endDate.date) {
      endDateString = obj.endDate.date
    }

    const tagIds = [] as ID[]
    if (obj.tags) {
      obj.tags.forEach((element) => {
        tagIds.push(ID.fromResponseObject(element, 'tags'))
      })
    }

    const documentIds = [] as ID[]
    if (obj.documents) {
      obj.documents.forEach((element) => {
        documentIds.push(ID.fromResponseObject(element, 'documents'))
      })
    }

    const customization = new InitiativeCustomization(
      ID.fromResponseObject(obj.id, 'initiative_customizations'),
      ID.fromResponseObject(obj.creator, 'users'),
      ID.fromResponseObject(obj.initiative, 'initiatives'),
      ID.fromResponseObject(obj.initiativeType, 'initiative_types'),
      ID.fromResponseObject(obj.plan, 'plans'),
      obj.isAllDay ? parseAllDayDate(startDateString) : new Date(startDateString),
      obj.isAllDay ? parseAllDayDate(endDateString) : new Date(endDateString),
      obj.isAllDay,
      tagIds,
      documentIds,
      obj.summary,
      obj.additionalSummaryUrl
    )
    customization.initiativeSummary = obj.initiativeSummary
      ? { id: ID.fromResponseObject(obj.initiativeSummary['@id'], 'initiative_customization_summaries'), contentUrl: obj.initiativeSummary.contentUrl }
      : null
    return customization
  }

  public forRequestObject() {
    return {
      id: this.id.intID == 0 ? null : this.id.apiID,
      creator: this.creatorId.intID == 0 ? null : this.creatorId.apiID,
      startDate: isValidDate(this.startDate) ? this.startDate : new Date(Date.now()),
      endDate: isValidDate(this.endDate) ? this.endDate : new Date(Date.now()),
      initiative: this.initiativeId.intID == 0 ? null : this.initiativeId.apiID,
      initiativeType: this.initiativeTypeId.intID == 0 ? null : this.initiativeTypeId.apiID,
      tags: this.tags.map((tag) => tag.id.apiID ),
      plan: this.planId.intID == 0 ? null : this.planId.apiID,
      isAllDay: this.isAllDay,
      documents: this.documents.map((document) => document.id.apiID),
      additionalSummaryContent: this.summary ? this.summary : null
    }
  }
}
